import type { UppFloorPlanElement } from '../plan/floor-plan-element';
import { UppFloorPlanTable } from '../plan/floor-plan-element';
import {
  roundFloorPlanGeometryCm,
  roundNormalizedPlanRotationDeg,
} from './floor-plan-konva-sync';

/** Tables created as `Konva.Rect` (no chairs, rectangular shape). */
export function isFloorPlanTableRectNodeModel(el: UppFloorPlanTable): boolean {
  return el.chairCount <= 0 && el.tableShape === 'RECT';
}

/**
 * Table visual center in Konva parent coordinates (`contentGroup` local px).
 * Group with centered pivot: node `x`/`y`. Rect (or top-left pivot node): center via
 * `getTransform().point(width/2, height/2)` in **local coordinates without pre-multiplying by scale** — the
 * `getTransform()` matrix already applies `scaleX`/`scaleY`; mixing with `width*scale` shifts the center after
 * transformer/group scaling.
 */
export function tableVisualCenterParentPx(node: any): { x: number; y: number } {
  const cls = typeof node.getClassName === 'function' ? node.getClassName() : '';
  if (cls === 'Group') {
    return { x: node.x(), y: node.y() };
  }
  const wLoc = Math.max(1, node.width?.() ?? 0);
  const hLoc = Math.max(1, node.height?.() ?? 0);
  return node.getTransform().point({ x: wLoc / 2, y: hLoc / 2 });
}

/** Reads node width/height/rotation after a Transformer gesture (same as `syncKonvaNodeGeometryToElement` resize). */
export function readTableKonvaNodeSizeRotationCm(
  node: any,
  scalePxPerCm: number,
): { wCm: number; hCm: number; rotDeg: number } {
  const sx = node.scaleX?.() ?? 1;
  const sy = node.scaleY?.() ?? 1;
  let wPx = node.width() * sx;
  let hPx = node.height() * sy;
  wPx = Math.max(1, wPx);
  hPx = Math.max(1, hPx);
  return {
    wCm: roundFloorPlanGeometryCm(wPx / scalePxPerCm),
    hCm: roundFloorPlanGeometryCm(hPx / scalePxPerCm),
    rotDeg: roundNormalizedPlanRotationDeg(node.rotation()),
  };
}

/**
 * Top-left corner (px in parent) of a `Rect` with Konva **clockwise** rotation around that corner,
 * given the desired geometric center in parent px.
 */
export function rectTopLeftPxFromCenterAndSizeRotationCw(
  cxPx: number,
  cyPx: number,
  wPx: number,
  hPx: number,
  rotationDeg: number,
): { x: number; y: number } {
  const rad = (rotationDeg * Math.PI) / 180;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  const lx = wPx / 2;
  const ly = hPx / 2;
  const rx = lx * cos + ly * sin;
  const ry = -lx * sin + ly * cos;
  return { x: cxPx - rx, y: cyPx - ry };
}

/**
 * Shortest angular delta from `fromDeg` to `toDeg` in degrees (result in (-180, 180]),
 * used to compose group rotation: `snap[i] + delta` keeps relative offsets between items.
 */
export function shortestRotationDeltaDeg(fromDeg: number, toDeg: number): number {
  let d = toDeg - fromDeg;
  while (d > 180) {
    d -= 360;
  }
  while (d <= -180) {
    d += 360;
  }
  return d;
}

/**
 * Rotates vector `(dx, dy)` in parent coordinates (**Y down**), angle **phi** in **clockwise** degrees
 * (same convention as Konva `rotation` and {@link rectTopLeftPxFromCenterAndSizeRotationCw}).
 */
export function rotateParentVectorCwDeg(dx: number, dy: number, phiDeg: number): { x: number; y: number } {
  const rad = (phiDeg * Math.PI) / 180;
  const c = Math.cos(rad);
  const s = Math.sin(rad);
  return { x: dx * c + dy * s, y: -dx * s + dy * c };
}

/** Rotates point `(x, y)` around `(pivotX, pivotY)` with the same convention as {@link rotateParentVectorCwDeg}. */
export function rotateParentPointCwAboutPivotDeg(
  x: number,
  y: number,
  pivotX: number,
  pivotY: number,
  phiDeg: number,
): { x: number; y: number } {
  const v = rotateParentVectorCwDeg(x - pivotX, y - pivotY, phiDeg);
  return { x: pivotX + v.x, y: pivotY + v.y };
}

/**
 * Top-left corner in **room cm** of a rectangle with Konva clockwise rotation around that corner,
 * given the desired geometric center (same convention as {@link rectTopLeftPxFromCenterAndSizeRotationCw}).
 */
export function rectTopLeftCmFromCenterAndSizeRotationCw(
  cxCm: number,
  cyCm: number,
  wCm: number,
  hCm: number,
  rotationDeg: number,
): { x: number; y: number } {
  const rad = (rotationDeg * Math.PI) / 180;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  const lx = wCm / 2;
  const ly = hCm / 2;
  const rx = lx * cos + ly * sin;
  const ry = -lx * sin + ly * cos;
  return { x: cxCm - rx, y: cyCm - ry };
}

/**
 * Visual center of the table in **room cm**, aligned with {@link tableVisualCenterParentPx} in the current Konva node.
 */
export function tableVisualCenterCm(el: UppFloorPlanTable): { xCm: number; yCm: number } {
  if (isFloorPlanTableRectNodeModel(el)) {
    const rad = (el.rotationDeg * Math.PI) / 180;
    const cos = Math.cos(rad);
    const sin = Math.sin(rad);
    const lx = el.widthCm / 2;
    const ly = el.heightCm / 2;
    const rx = lx * cos + ly * sin;
    const ry = -lx * sin + ly * cos;
    return { xCm: el.xCm + rx, yCm: el.yCm + ry };
  }
  return {
    xCm: el.xCm + el.widthCm / 2,
    yCm: el.yCm + el.heightCm / 2,
  };
}

/**
 * Logical bbox center in **cm** (tables: same rule as {@link tableVisualCenterCm}; bars/furniture/architecture:
 * Konva pivot at top-left corner of `Group` + rotation).
 */
export function floorPlanElementBBoxVisualCenterCm(el: UppFloorPlanElement): { xCm: number; yCm: number } {
  if (el instanceof UppFloorPlanTable) {
    return tableVisualCenterCm(el);
  }
  const rad = (el.rotationDeg * Math.PI) / 180;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  const lx = el.widthCm / 2;
  const ly = el.heightCm / 2;
  const rx = lx * cos + ly * sin;
  const ry = -lx * sin + ly * cos;
  return { xCm: el.xCm + rx, yCm: el.yCm + ry };
}

/**
 * Centroid (arithmetic mean) of selection {@link floorPlanElementBBoxVisualCenterCm}.
 * Useful as reference pivot for **analytical rigid rotation** of the group in room cm (contrast with Konva state after
 * a group `Transformer` — actual transformer pivot may be selection AABB center, not this centroid).
 */
export function floorPlanSelectionBBoxVisualCentroidCm(
  elements: readonly UppFloorPlanElement[],
): { xCm: number; yCm: number } {
  if (elements.length === 0) {
    return { xCm: 0, yCm: 0 };
  }
  let sx = 0;
  let sy = 0;
  for (const el of elements) {
    const c = floorPlanElementBBoxVisualCenterCm(el);
    sx += c.xCm;
    sy += c.yCm;
  }
  const n = elements.length;
  return { xCm: sx / n, yCm: sy / n };
}

/**
 * Visual center in parent px: tables use {@link tableVisualCenterParentPx}; others via `getTransform().point`
 * with local halves `width()/2`, `height()/2` (without × `scaleX`/`scaleY`; see {@link tableVisualCenterParentPx}).
 */
export function elementVisualCenterParentPx(node: any, el: UppFloorPlanElement): { x: number; y: number } {
  if (el instanceof UppFloorPlanTable) {
    return tableVisualCenterParentPx(node);
  }
  const wLoc = Math.max(1, node.width?.() ?? 0);
  const hLoc = Math.max(1, node.height?.() ?? 0);
  return node.getTransform().point({ x: wLoc / 2, y: hLoc / 2 });
}

/**
 * Assigns same width, height and rotation to each table by **mutating** the model, preserving each visual center.
 * Valid for one or multiple tables (e.g. numeric input from host).
 */
export function applyUniformTableGeometryCm(
  tables: readonly UppFloorPlanTable[],
  widthCm: number,
  heightCm: number,
  rotationDeg: number,
): void {
  if (tables.length === 0) {
    return;
  }
  const w = roundFloorPlanGeometryCm(widthCm);
  const h = roundFloorPlanGeometryCm(heightCm);
  const r = roundNormalizedPlanRotationDeg(rotationDeg);
  if (!(w > 0 && h > 0 && Number.isFinite(r))) {
    return;
  }
  for (const el of tables) {
    const { xCm: cx, yCm: cy } = tableVisualCenterCm(el);
    el.widthCm = w;
    el.heightCm = h;
    el.rotationDeg = r;
    if (isFloorPlanTableRectNodeModel(el)) {
      const top = rectTopLeftCmFromCenterAndSizeRotationCw(cx, cy, w, h, r);
      el.xCm = roundFloorPlanGeometryCm(top.x);
      el.yCm = roundFloorPlanGeometryCm(top.y);
    } else {
      el.xCm = roundFloorPlanGeometryCm(cx - w / 2);
      el.yCm = roundFloorPlanGeometryCm(cy - h / 2);
    }
  }
}

/**
 * Same as {@link applyUniformTableGeometryCm} but for **bars, furniture and architecture** (bbox with top-left pivot
 * in Konva). Must not be used with {@link UppFloorPlanTable}.
 */
export function applyUniformBBoxGeometryCm(
  elements: readonly UppFloorPlanElement[],
  widthCm: number,
  heightCm: number,
  rotationDeg: number,
): void {
  if (elements.length === 0) {
    return;
  }
  for (const el of elements) {
    if (el instanceof UppFloorPlanTable) {
      return;
    }
  }
  const w = roundFloorPlanGeometryCm(widthCm);
  const h = roundFloorPlanGeometryCm(heightCm);
  const r = roundNormalizedPlanRotationDeg(rotationDeg);
  if (!(w > 0 && h > 0 && Number.isFinite(r))) {
    return;
  }
  for (const el of elements) {
    const { xCm: cx, yCm: cy } = floorPlanElementBBoxVisualCenterCm(el);
    el.widthCm = w;
    el.heightCm = h;
    el.rotationDeg = r;
    const top = rectTopLeftCmFromCenterAndSizeRotationCw(cx, cy, w, h, r);
    el.xCm = roundFloorPlanGeometryCm(top.x);
    el.yCm = roundFloorPlanGeometryCm(top.y);
  }
}

/**
 * After group `Transformer` on **>= 2** non-table elements (same `kind`): same width/height as the first
 * one (uniform group scaling). **Position:** `xCm`/`yCm` comes from `node.getTransform().point({ x: 0, y: 0 })`
 * in parent px (same rule as `syncKonvaNodeGeometryToElement` for bbox with top-left pivot), instead of rebuilding
 * corner from center — avoids `Group` drift (e.g. L sofas) when center reading and Konva matrix differ in subpixel.
 *
 * **Rotation:** if `modelRotationDegAtGestureStart` has same length as `elements`, those values are used
 * (captured from model at `transformstart`); this avoids Konva leaving wrong `node.rotation()` after group **scale**
 * with mixed angles (e.g. 0 and 180 degrees). With **`rotater`** anchor and snapshot, each item rotation is
 * `snapshot[i] + delta`, where `delta` comes from the **first** node (Konva can sometimes equalize `rotation()`
 * across nodes and break 0/90 patterns). Without snapshot, rotation is read from each node.
 *
 * @param groupRotationGesture - If true, use same branch as `'rotater'` anchor when rotation handle was used
 * during gesture but `transformerActiveAnchor` did not reach `transformend` reliably.
 *
 * **Inference:** when snapshot exists and the **first** node changed angle vs `snapshot[0]` (beyond a minimum threshold),
 * apply same `snapshot[i] + delta` branch even if neither anchor nor `groupRotationGesture` indicates `rotater`
 * (e.g. Konva did not emit transform with reliable anchor). This avoids mixing already-rotated centers with frozen
 * snapshot `rotationDeg`.
 *
 * Internal inference threshold: 0.05 degrees (above numeric noise, below real rotations).
 */
const GROUP_TRANSFORM_INFER_ROTATION_MIN_DELTA_DEG = 0.05;

export function uniformSyncMultiBBoxGroupTransform(
  nodes: any[],
  elements: readonly UppFloorPlanElement[],
  scalePxPerCm: number,
  modelRotationDegAtGestureStart?: readonly number[] | null,
  /** `getActiveAnchor()` from Transformer; `'rotater'` activates delta+snapshot branch. */
  transformerActiveAnchor?: string | null,
  groupRotationGesture?: boolean,
): void {
  if (nodes.length < 2 || nodes.length !== elements.length) {
    return;
  }
  const useModelRot =
    modelRotationDegAtGestureStart != null &&
    modelRotationDegAtGestureStart.length === elements.length;
  const firstNode = nodes[0];
  if (!firstNode) {
    return;
  }
  const primaryKonvaRotDeg = readTableKonvaNodeSizeRotationCm(firstNode, scalePxPerCm).rotDeg;
  const inferredDeltaFromPrimary =
    useModelRot
      ? shortestRotationDeltaDeg((modelRotationDegAtGestureStart[0] ?? 0), primaryKonvaRotDeg)
      : null;
  const rotaterExplicit = transformerActiveAnchor === 'rotater' || groupRotationGesture === true;
  const useRotationDeltaBranch =
    useModelRot &&
    inferredDeltaFromPrimary != null &&
    (rotaterExplicit || Math.abs(inferredDeltaFromPrimary) >= GROUP_TRANSFORM_INFER_ROTATION_MIN_DELTA_DEG);
  const rotationDeltaFromPrimary = useRotationDeltaBranch ? inferredDeltaFromPrimary : null;
  const ref = readTableKonvaNodeSizeRotationCm(firstNode, scalePxPerCm);
  for (let i = 0; i < elements.length; i++) {
    const el = elements[i];
    const node = nodes[i];
    if (!el || !node) {
      continue;
    }
    const each = readTableKonvaNodeSizeRotationCm(node, scalePxPerCm);
    let rotDeg: number;
    if (useModelRot && rotationDeltaFromPrimary != null) {
      rotDeg = roundNormalizedPlanRotationDeg((modelRotationDegAtGestureStart[i] ?? 0) + rotationDeltaFromPrimary);
    } else if (useModelRot) {
      rotDeg = roundNormalizedPlanRotationDeg(modelRotationDegAtGestureStart[i] ?? 0);
    } else {
      rotDeg = each.rotDeg;
    }
    el.widthCm = ref.wCm;
    el.heightCm = ref.hCm;
    el.rotationDeg = rotDeg;
    const pTopLeft =
      typeof node.getTransform === 'function' ? node.getTransform().point({ x: 0, y: 0 }) : { x: 0, y: 0 };
    el.xCm = roundFloorPlanGeometryCm(pTopLeft.x / scalePxPerCm);
    el.yCm = roundFloorPlanGeometryCm(pTopLeft.y / scalePxPerCm);
  }
}

/**
 * After group `Transformer` with **>= 2** elements of the **same** `kind`: tables -> {@link uniformSyncMultiTableGroupTransform};
 * other transformables -> {@link uniformSyncMultiBBoxGroupTransform}.
 *
 * @param modelRotationDegAtGestureStart - Model rotations at gesture start (group scale or group rotation).
 * @param transformerActiveAnchor - Gesture handle used (e.g. `'rotater'`). Must be captured at **`transformstart`**
 *   (`getActiveAnchor()`), not only at `transformend`, because Konva may reset anchor to `null` when closing gesture.
 * @param groupRotationGesture - See {@link uniformSyncMultiBBoxGroupTransform}.
 */
export function uniformSyncMultiUniformKindGroupTransform(
  nodes: any[],
  elements: readonly UppFloorPlanElement[],
  scalePxPerCm: number,
  modelRotationDegAtGestureStart?: readonly number[] | null,
  transformerActiveAnchor?: string | null,
  groupRotationGesture?: boolean,
): void {
  if (nodes.length < 2 || nodes.length !== elements.length) {
    return;
  }
  if (elements.every((e) => e instanceof UppFloorPlanTable)) {
    uniformSyncMultiTableGroupTransform(
      nodes,
      elements as UppFloorPlanTable[],
      scalePxPerCm,
      modelRotationDegAtGestureStart,
      transformerActiveAnchor,
      groupRotationGesture,
    );
    return;
  }
  if (elements.some((e) => e instanceof UppFloorPlanTable)) {
    return;
  }
  uniformSyncMultiBBoxGroupTransform(
    nodes,
    elements,
    scalePxPerCm,
    modelRotationDegAtGestureStart,
    transformerActiveAnchor,
    groupRotationGesture,
  );
}

/**
 * After group `Transformer` on multiple tables: same `widthCm`/`heightCm` (reference = first table)
 * while preserving each table visual center in parent space.
 *
 * **Rotation:** with snapshot and **without** `rotater`, model per-item rotation is used (group scale). With **`rotater`**
 * (or inferred delta >= threshold), uses `snapshot[i] + delta` from first node. Without snapshot, uses first-table `rotationDeg` for all.
 * Same **first-node delta inference** as {@link uniformSyncMultiBBoxGroupTransform}.
 *
 * **Size:** in group-rotation branch (`snapshot[i] + delta`), each table keeps width/height read from **its** Konva node;
 * for scale or other gestures, first-table reference is still used (same `widthCm`/`heightCm` for all).
 *
 * @param groupRotationGesture - See {@link uniformSyncMultiBBoxGroupTransform}.
 */
export function uniformSyncMultiTableGroupTransform(
  nodes: any[],
  tables: UppFloorPlanTable[],
  scalePxPerCm: number,
  modelRotationDegAtGestureStart?: readonly number[] | null,
  transformerActiveAnchor?: string | null,
  groupRotationGesture?: boolean,
): void {
  if (nodes.length < 2 || nodes.length !== tables.length) {
    return;
  }
  const useModelRot =
    modelRotationDegAtGestureStart != null && modelRotationDegAtGestureStart.length === tables.length;
  const firstTableNode = nodes[0];
  if (!firstTableNode) {
    return;
  }
  const primaryKonvaRotDeg = readTableKonvaNodeSizeRotationCm(firstTableNode, scalePxPerCm).rotDeg;
  const inferredDeltaFromPrimary =
    useModelRot
      ? shortestRotationDeltaDeg((modelRotationDegAtGestureStart[0] ?? 0), primaryKonvaRotDeg)
      : null;
  const rotaterExplicit = transformerActiveAnchor === 'rotater' || groupRotationGesture === true;
  const useRotationDeltaBranch =
    useModelRot &&
    inferredDeltaFromPrimary != null &&
    (rotaterExplicit || Math.abs(inferredDeltaFromPrimary) >= GROUP_TRANSFORM_INFER_ROTATION_MIN_DELTA_DEG);
  const rotationDeltaFromPrimary = useRotationDeltaBranch ? inferredDeltaFromPrimary : null;
  const preservePerTableSize = rotationDeltaFromPrimary != null;
  const centersPx = nodes.map((n) => tableVisualCenterParentPx(n));
  const ref = readTableKonvaNodeSizeRotationCm(firstTableNode, scalePxPerCm);

  for (let i = 0; i < tables.length; i++) {
    const el = tables[i];
    const node = nodes[i];
    const center = centersPx[i];
    if (!el || !node || !center) {
      continue;
    }
    const { x: cxPx, y: cyPx } = center;
    let rotDeg: number;
    if (useModelRot && rotationDeltaFromPrimary != null) {
      rotDeg = roundNormalizedPlanRotationDeg((modelRotationDegAtGestureStart[i] ?? 0) + rotationDeltaFromPrimary);
    } else if (useModelRot) {
      rotDeg = roundNormalizedPlanRotationDeg(modelRotationDegAtGestureStart[i] ?? 0);
    } else {
      rotDeg = ref.rotDeg;
    }
    const sizeRef = preservePerTableSize ? readTableKonvaNodeSizeRotationCm(node, scalePxPerCm) : ref;
    const wCm = sizeRef.wCm;
    const hCm = sizeRef.hCm;
    const wPx = wCm * scalePxPerCm;
    const hPx = hCm * scalePxPerCm;
    el.widthCm = wCm;
    el.heightCm = hCm;
    el.rotationDeg = rotDeg;
    if (isFloorPlanTableRectNodeModel(el)) {
      const top = rectTopLeftPxFromCenterAndSizeRotationCw(cxPx, cyPx, wPx, hPx, rotDeg);
      el.xCm = roundFloorPlanGeometryCm(top.x / scalePxPerCm);
      el.yCm = roundFloorPlanGeometryCm(top.y / scalePxPerCm);
    } else {
      el.xCm = roundFloorPlanGeometryCm(cxPx / scalePxPerCm - wCm / 2);
      el.yCm = roundFloorPlanGeometryCm(cyPx / scalePxPerCm - hCm / 2);
    }
  }
}
